import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  OnChanges,
  OnInit,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';
import { AuthService } from 'src/app/Core/auth/services/auth/auth.service';
import { NotificationService } from '../../../account/notification/services/notification.service';

import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuctionService } from '../../_services/auction/auction.service';
import { ToastService } from 'src/app/Shared/services/toast/toast.service';
import { TranslationUpdater } from 'src/app/Shared/helpers/translation-updater';
import { LanguageService } from 'src/app/Shared/services/language/language.service';

interface Option {
  value: string;
  label: string;
}

interface NoteField {
  id: string;
  label: string;
  type: 'single' | 'multiple' | 'text';
  options?: Option[];
  placeholder?: string;
  buttonText?: string;
  translationDataKey: string;
  buttonTextTranslationDataKey: string;
}

interface DropdownState {
  id: string;
  isOpen: boolean;
  selectedItems: string[];
  textValue?: string;
}

@Component({
    selector: 'app-horse-notes',
        standalone: true,
imports: [
    MatIcon,
    MatListModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatRadioModule,
    TranslatePipe
],
    templateUrl: './horse-notes.component.html',
    styleUrl: './horse-notes.component.scss'
})
export class HorseNotesComponent implements OnChanges, OnInit, OnDestroy {
  @Input() horseNotes: any;
  @Input() auctionItemId: any;

  @Output() notesChanged = new EventEmitter<any>();
  @Output() notesUpdated = new EventEmitter<any>(); // New output for immediate data

  isLoading: boolean = false;
  isAddedSucessfully: boolean = false;
  isSaving: boolean = false;
  lastSaveTime: Date | null = null;
  autoSaveSubject = new Subject<void>();
  fieldSavingStates: { [key: string]: boolean } = {};

  // Dynamic configuration for all note fields
  noteFields: NoteField[] = [];

  // Dynamic state management
  dropdownStates: { [key: string]: DropdownState } = {};
  isDoneButtonDisabled: boolean = true;
  private translationUpdater?: TranslationUpdater;

  constructor(
    private elementRef: ElementRef,
    private toastService: ToastService,
    private auctionService: AuctionService,
    private translate: TranslateService,
    private languageService: LanguageService
  ) {
    this.setupAutoSave();
  }

  ngOnInit(): void {
    this.initializeNoteFields();
    this.initializeDropdownStates();

    // Set up translation updater to refresh fields when language changes
    this.translationUpdater = new TranslationUpdater(
      this.languageService,
      () => {
        this.initializeNoteFields();
        // Re-initialize dropdown states to match new field structure
        this.initializeDropdownStates();
        // Reload existing data if available to maintain state
        if (this.horseNotes) {
          this.loadExistingData(this.horseNotes);
        }
      }
    );
  }

  // Add OnChanges implementation to handle horseNotes updates
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['horseNotes']) {
      // Reset component state when horseNotes changes
      this.resetComponentState();

      // Load the new data
      if (this.horseNotes) {
        this.loadExistingData(this.horseNotes);
      }
    }
  }

  ngAfterViewInit() {
    // Load existing data if provided
    if (this.horseNotes) {
      this.loadExistingData(this.horseNotes);
    }
  }

  private initializeNoteFields(): void {
    this.noteFields = [
      {
        id: 'rates',
        label: this.translate.instant('horse_info.rates'),
        type: 'single',
        buttonText: this.translate.instant('horse_info.choose_rating'),
        options: [
          { value: 'A+', label: 'A+' },
          { value: 'A', label: 'A' },
          { value: 'B+', label: 'B+' },
          { value: 'B', label: 'B' },
          { value: 'C', label: 'C' },
          { value: 'D', label: 'D' },
        ],
        translationDataKey: 'horse_info_rates',
        buttonTextTranslationDataKey: 'horse_info_choose_rating',
      },
      {
        id: 'conformation',
        label: this.translate.instant('horse_info.conformation'),
        type: 'multiple',
        buttonText: this.translate.instant('horse_info.choose_conformation'),
        options: [
          { value: 'Balanced', label: this.translate.instant('horse_info.conformation_balanced') },
          { value: 'Great Walk', label: this.translate.instant('horse_info.conformation_great_walk') },
          { value: 'Strong Hip', label: this.translate.instant('horse_info.conformation_strong_hip') },
          { value: 'Offset Knees', label: this.translate.instant('horse_info.conformation_offset_knees') },
          { value: 'Back at Knees', label: this.translate.instant('horse_info.conformation_back_at_knees') },
          { value: 'Toes In', label: this.translate.instant('horse_info.conformation_toes_in') },
          { value: 'Toes Out', label: this.translate.instant('horse_info.conformation_toes_out') },
          { value: 'Weak Hocks', label: this.translate.instant('horse_info.conformation_weak_hocks') },
          { value: 'Long Pasterns', label: this.translate.instant('horse_info.conformation_long_pasterns') },
          { value: 'Straight Pasterns', label: this.translate.instant('horse_info.conformation_straight_pasterns') },
        ],
        translationDataKey: 'horse_info_confirmation',
        buttonTextTranslationDataKey: 'horse_info_choose_conformation',
      },
      {
        id: 'scope',
        label: this.translate.instant('horse_info.scoop'),
        type: 'multiple',
        buttonText: this.translate.instant('horse_info.choose_scope'),
        options: [
          { value: 'Scope 1', label: this.translate.instant('horse_info.scope_1') },
          { value: 'Scope 2A', label: this.translate.instant('horse_info.scope_2a') },
          { value: 'Scope 2B', label: this.translate.instant('horse_info.scope_2b') },
          { value: 'Scope 3A', label: this.translate.instant('horse_info.scope_3a') },
          { value: 'Scope 3B', label: this.translate.instant('horse_info.scope_3b') },
          { value: 'Scope other', label: this.translate.instant('horse_info.scope_other') },
          { value: 'X-Ray (Normal)', label: this.translate.instant('horse_info.xray_normal') },
          { value: 'X-Ray (Other)', label: this.translate.instant('horse_info.xray_other') },
        ],
        translationDataKey: 'horse_info_scoop',
        buttonTextTranslationDataKey: 'horse_info_choose_scope',
      },
      {
        id: 'notes',
        label: this.translate.instant('horse_info.horse_note'),
        type: 'text',
        buttonText: this.translate.instant('horse_info.write_note'),
        placeholder: this.translate.instant('placeholders.enter_notes'),
        translationDataKey: 'horse_info_horse_note',
        buttonTextTranslationDataKey: 'horse_info_write_note',
      },
    ];
  }

  private initializeDropdownStates(): void {
    this.noteFields?.forEach((field) => {
      this.dropdownStates[field.id] = {
        id: field.id,
        isOpen: false,
        selectedItems: [],
        textValue: '',
      };
    });
  }

  private setupAutoSave(): void {
    // Debounce auto-save to prevent too many API calls
    // For text fields, wait 1 second after user stops typing
    // For checkboxes/radio buttons, save immediately
    this.autoSaveSubject
      .pipe(
        debounceTime(1500) // Wait 1 second before saving
      )
      .subscribe(() => {
        this.saveHorseNote();
      });
  }

  getDropdownClasses(fieldId: string): string {
    const baseClasses = 'dropdown-panel';
    const isOpen = this.dropdownStates[fieldId]?.isOpen;
    const position = this.dropdownPositions[fieldId] || 'bottom';

    let classes = baseClasses;
    if (isOpen) classes += ' show';
    classes += ` dropdown-${position}`;

    return classes;
  }

  private initializedFields: Set<string> = new Set();

  // Update the loadFieldFromAPI method to mark fields as initialized
  private loadFieldFromAPI(fieldId: string): void {
    if (!this.horseNotes) return;

    const state = this.dropdownStates[fieldId];
    let value;

    switch (fieldId) {
      case 'rates':
        value = this.horseNotes.rating;
        break;
      case 'conformation':
        value = this.horseNotes.confirmation?.types || [];
        break;
      case 'scope':
        value = this.horseNotes.scope?.types || [];
        break;
      case 'notes':
        value = this.horseNotes.notes;
        break;
    }

    const field = this.noteFields.find((f) => f.id === fieldId);
    if (field?.type === 'text') {
      state.textValue = value || '';
    } else if (field?.type === 'single') {
      state.selectedItems = value ? [value] : [];
    } else {
      state.selectedItems = Array.isArray(value) ? value : [];
    }

    // Mark this field as initialized
    this.initializedFields.add(fieldId);
  }
  isTextChanged: boolean = false;
  onTextChange(fieldId: string, value: string): void {
    const state = this.dropdownStates[fieldId];
    state.textValue = value;
    this.isDoneButtonDisabled = false;
    this.isTextChanged = true;
  }

  isFieldSelected(fieldId: string, value: string): boolean {
    if (this.initializedFields.has(fieldId)) {
      return this.dropdownStates[fieldId].selectedItems.includes(value);
    }

    // Only fall back to API data if component state hasn't been initialized yet
    if (this.horseNotes) {
      switch (fieldId) {
        case 'rates':
          return this.horseNotes.rating === value;
        case 'conformation':
          return (this.horseNotes.confirmation?.types || []).includes(value);
        case 'scope':
          return (this.horseNotes.scope?.types || []).includes(value);
        default:
          return false;
      }
    }

    return false;
  }
  hasFieldValue(fieldId: string): boolean {
    const state = this.dropdownStates[fieldId];
    const field = this.noteFields.find((f) => f.id === fieldId);

    if (field?.type === 'text') {
      return !!state.textValue?.trim();
    }

    // For selection fields, check current state
    return state.selectedItems.length > 0;
  }

  getFieldDisplayValue(fieldId: string): string {
    const state = this.dropdownStates[fieldId];
    const field = this.noteFields.find((f) => f.id === fieldId);

    if (field?.type === 'text') {
      return state.textValue || '';
    }

    if (field?.type === 'single' && state.selectedItems.length > 0) {
      return state.selectedItems[0];
    }

    return state.selectedItems.join(', ');
  }

  getSelectedLabels(fieldId: string): string[] {
    const state = this.dropdownStates[fieldId];
    const field = this.noteFields.find((f) => f.id === fieldId);

    if (!field?.options) return [];

    return state.selectedItems.map((value) => {
      const option = field.options!.find((opt) => opt.value === value);
      return option?.label || value;
    });
  }

  // Get all form data
  getAllFormData(): { [key: string]: any } {
    const result: { [key: string]: any } = {};

    this.noteFields.forEach((field) => {
      const state = this.dropdownStates[field.id];

      if (field.type === 'text') {
        result[field.id] = state.textValue;
      } else if (field.type === 'single') {
        result[field.id] = state.selectedItems[0] || null;
      } else {
        result[field.id] = state.selectedItems;
      }
    });

    return result;
  }

  // Check if any field has data based on API structure
  hasAnyData(): boolean {
    return this.noteFields.some((field) => this.hasFieldValue(field.id));
  }

  getDisplayLabels(fieldId: string): string[] {
    const field = this.noteFields.find((f) => f.id === fieldId);
    if (!field?.options) return [];

    // Always use current dropdown state if field has been initialized or is currently open
    if (
      this.initializedFields.has(fieldId) ||
      this.dropdownStates[fieldId]?.isOpen
    ) {
      const state = this.dropdownStates[fieldId];
      return state.selectedItems.map((value) => {
        const option = field.options!.find((opt) => opt.value === value);
        return option?.label || value;
      });
    }

    // If field hasn't been initialized and dropdown is closed, show API data
    if (this.hasFieldValueFromAPI(fieldId)) {
      return this.getSelectedLabelsFromAPI(fieldId);
    }

    // Otherwise show empty
    return [];
  }
  shouldShowFieldDisplay(fieldId: string): boolean {
    // If dropdown is open, show display based on current state (even if empty)
    if (this.dropdownStates[fieldId]?.isOpen) {
      return true; // Always show the display area when dropdown is open
    }

    // If field has been initialized, show based on current state
    if (this.initializedFields.has(fieldId)) {
      return this.hasFieldValue(fieldId);
    }

    // If field hasn't been initialized, show based on API data
    return this.hasFieldValueFromAPI(fieldId);
  }

  hasFieldValueFromAPI(fieldId: string): boolean {
    if (!this.horseNotes) return false;

    switch (fieldId) {
      case 'rates':
        return !!this.horseNotes.rating;
      case 'conformation':
        return (
          Array.isArray(this.horseNotes.confirmation?.types) &&
          this.horseNotes.confirmation.types.length > 0
        );
      case 'scope':
        return (
          Array.isArray(this.horseNotes.scope?.types) &&
          this.horseNotes.scope.types.length > 0
        );
      case 'notes':
        return !!this.horseNotes.notes?.trim();
      default:
        return false;
    }
  }

  // Get display value from API data
  getFieldDisplayValueFromAPI(fieldId: string): string {
    if (!this.horseNotes) return '';

    switch (fieldId) {
      case 'rates':
        return this.horseNotes.rating || '';
      case 'conformation':
        return (this.horseNotes.confirmation?.types || []).join(', ');
      case 'scope':
        return (this.horseNotes.scope?.types || []).join(', ');
      case 'notes':
        return this.horseNotes.notes || '';
      default:
        return '';
    }
  }

  // Get selected labels from API data
  getSelectedLabelsFromAPI(fieldId: string): string[] {
    if (!this.horseNotes) return [];

    const field = this.noteFields.find((f) => f.id === fieldId);
    if (!field?.options) return [];

    let selectedValues: string[] = [];

    switch (fieldId) {
      case 'conformation':
        selectedValues = this.horseNotes.confirmation?.types || [];
        break;
      case 'scope':
        selectedValues = this.horseNotes.scope?.types || [];
        break;
      default:
        selectedValues = [];
    }

    return selectedValues.map((value) => {
      const option = field.options!.find((opt) => opt.value === value);
      return option?.label || value;
    });
  }

  // Load existing data (useful for editing)
  loadExistingData(data: any): void {
    this.noteFields.forEach((field) => {
      const state = this.dropdownStates[field.id];
      let value;

      // Map the API response to component data structure
      switch (field.id) {
        case 'rates':
          value = data.rating;
          break;
        case 'conformation':
          value = data.confirmation?.types || [];
          break;
        case 'scope':
          value = data.scope?.types || [];
          break;
        case 'notes':
          value = data.notes;
          break;
        default:
          value = data[field.id];
      }

      if (field.type === 'text') {
        state.textValue = value || '';
      } else if (field.type === 'single') {
        state.selectedItems = value ? [value] : [];
      } else {
        state.selectedItems = Array.isArray(value) ? value : [];
      }

      // Mark field as initialized since we loaded data into it
      this.initializedFields.add(field.id);
    });
  }

  // Track by function for ngFor performance
  trackByFieldId(index: number, field: NoteField): string {
    return field.id;
  }

  trackByValue(index: number, option: Option): string {
    return option.value;
  }

  isFieldSaving(fieldId: string): boolean {
    return this.fieldSavingStates[fieldId] || false;
  }

  private setFieldSaving(fieldId: string, saving: boolean): void {
    this.fieldSavingStates[fieldId] = saving;
  }
  shouldSave(): boolean {
    // If we have any current data, we should save
    if (this.hasAnyData()) {
      return true;
    }

    // If we don't have current data but we have API data, we should save to clear it
    return this.noteFields.some((field) => this.hasFieldValueFromAPI(field.id));
  }

  isTextNotePanel: boolean = false;
  toggleTextNotePanel(fieldId?: string, event?: Event): void {
    this.isTextNotePanel = !this.isTextNotePanel;
    this.closeAllDropdowns();

    // Calculate position for text note panel
    if (this.isTextNotePanel && fieldId && event) {
      const triggerElement = event.target as HTMLElement;
      const buttonElement = triggerElement.closest('button') || triggerElement;
      this.dropdownPositions[fieldId] = this.calculateDropdownPosition(
        fieldId,
        buttonElement
      );
    } else if (!this.isTextNotePanel && fieldId) {
      // Clean up position when closing
      delete this.dropdownPositions[fieldId];
    }
  }

  ngOnDestroy(): void {
    // Clean up the auto-save subject
    this.autoSaveSubject.complete();

    // Clean up translation updater
    if (this.translationUpdater) {
      this.translationUpdater.destroy();
    }
  }

  dropdownPositions: { [key: string]: 'top' | 'bottom' } = {};

  // Method to calculate dropdown position
  calculateDropdownPosition(
    fieldId: string,
    triggerElement: HTMLElement
  ): 'top' | 'bottom' {
    const rect = triggerElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 300; // Estimated dropdown height

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // If there's enough space below, open downward
    if (spaceBelow >= dropdownHeight) {
      return 'bottom';
    }
    // If there's more space above than below, open upward
    else if (spaceAbove > spaceBelow) {
      return 'top';
    }
    // Default to bottom if neither condition is clearly better
    else {
      return 'bottom';
    }
  }

  // Add a new property to track fields that need saving
  private fieldsNeedingSave: Set<string> = new Set();

  onOptionSelect(fieldId: string, value: string, field: NoteField): void {
    const state = this.dropdownStates[fieldId];

    // Mark field as initialized when user interacts with it
    this.initializedFields.add(fieldId);

    if (field.type === 'single') {
      // For single selection (like rating)
      if (state.selectedItems.includes(value)) {
        // If it is, deselect it
        state.selectedItems = [];
      } else {
        // If it isn't, select it
        state.selectedItems = [value];
      }
      state.isOpen = false; // Close dropdown after selection

      // Mark field as needing save and trigger auto-save with loader
      this.fieldsNeedingSave.add(fieldId);
      this.triggerAutoSave(fieldId, true);
    } else if (field.type === 'multiple') {
      // For multiple selection (like conformation, scope)
      const isSelected = state.selectedItems.includes(value);
      if (isSelected) {
        state.selectedItems = state.selectedItems.filter(
          (item) => item !== value
        );
      } else {
        state.selectedItems = [...state.selectedItems, value];
      }

      // Mark field as needing save but don't trigger auto-save yet
      // Auto-save will be triggered when dropdown closes
      this.fieldsNeedingSave.add(fieldId);

      // Force change detection to update the display immediately
      // This ensures the labels update in real-time
    }
  }

  toggleDropdown(fieldId: string, event?: Event): void {
    const isCurrentlyOpen = this.dropdownStates[fieldId]?.isOpen;

    if (this.isTextNotePanel) {
      this.isTextNotePanel = false;
    }

    // Only load from API if the field has never been initialized/touched
    // If user has interacted with it before, respect their current state (even if empty)
    if (
      !isCurrentlyOpen &&
      this.hasFieldValueFromAPI(fieldId) &&
      !this.hasFieldValue(fieldId) &&
      !this.initializedFields.has(fieldId) // Only load if field hasn't been touched
    ) {
      this.loadFieldFromAPI(fieldId);
    }

    // Calculate position before opening
    if (!isCurrentlyOpen && event) {
      const triggerElement = event.target as HTMLElement;
      const buttonElement = triggerElement.closest('button') || triggerElement;
      this.dropdownPositions[fieldId] = this.calculateDropdownPosition(
        fieldId,
        buttonElement
      );
    }

    // Close all other dropdowns first and trigger auto-save for them if needed
    Object.keys(this.dropdownStates).forEach((key) => {
      if (key !== fieldId && this.dropdownStates[key].isOpen) {
        this.dropdownStates[key].isOpen = false;
        delete this.dropdownPositions[key];

        // Trigger auto-save only if this field needs saving
        if (this.fieldsNeedingSave.has(key)) {
          const field = this.noteFields.find((f) => f.id === key);
          if (field && (field.type === 'single' || field.type === 'multiple')) {
            this.triggerAutoSave(key, true);
          }
        }
      }
    });

    // If we're closing the current dropdown, trigger auto-save if needed
    if (isCurrentlyOpen && this.fieldsNeedingSave.has(fieldId)) {
      const field = this.noteFields.find((f) => f.id === fieldId);
      if (field && (field.type === 'single' || field.type === 'multiple')) {
        this.triggerAutoSave(fieldId, true);
      }
    }

    // Toggle the selected dropdown
    this.dropdownStates[fieldId].isOpen = !isCurrentlyOpen;

    // If closing, clean up position data
    if (isCurrentlyOpen) {
      delete this.dropdownPositions[fieldId];
    }
  }

  // Update closeAllDropdowns to trigger auto-save only for fields that need it
  closeAllDropdowns(): void {
    Object.keys(this.dropdownStates).forEach((key) => {
      if (this.dropdownStates[key].isOpen) {
        // Trigger auto-save only if this field needs saving
        if (this.fieldsNeedingSave.has(key)) {
          const field = this.noteFields.find((f) => f.id === key);
          if (field && (field.type === 'single' || field.type === 'multiple')) {
            this.triggerAutoSave(key, true);
          }
        }

        this.dropdownStates[key].isOpen = false;
        delete this.dropdownPositions[key];
      }
    });
  }

  // Update the document click handler
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      // Check if any dropdowns are open and need saving
      Object.keys(this.dropdownStates).forEach((key) => {
        if (
          this.dropdownStates[key].isOpen &&
          this.fieldsNeedingSave.has(key)
        ) {
          const field = this.noteFields.find((f) => f.id === key);
          if (field && (field.type === 'single' || field.type === 'multiple')) {
            this.triggerAutoSave(key, true);
          }
        }
      });

      if (this.isTextChanged) {
        this.loadExistingData(this.horseNotes);
        this.isTextChanged = false;
      }
      this.isTextNotePanel = false;
      this.closeAllDropdowns();
    }
  }

  // Update the window resize handler
  @HostListener('window:resize')
  onWindowResize(): void {
    // Trigger auto-save only for fields that need saving before closing dropdowns
    Object.keys(this.dropdownStates).forEach((fieldId) => {
      if (
        this.dropdownStates[fieldId]?.isOpen &&
        this.fieldsNeedingSave.has(fieldId)
      ) {
        const field = this.noteFields.find((f) => f.id === fieldId);
        if (field && (field.type === 'single' || field.type === 'multiple')) {
          this.triggerAutoSave(fieldId, true);
        }
      }
    });

    // Close all dropdowns
    Object.keys(this.dropdownStates).forEach((fieldId) => {
      if (this.dropdownStates[fieldId]?.isOpen) {
        this.dropdownStates[fieldId].isOpen = false;
        delete this.dropdownPositions[fieldId];
      }
    });
  }

  // Update triggerAutoSave to show loader only when actually saving
  private triggerAutoSave(fieldId: string, immediate: boolean = false): void {
    if (!this.auctionItemId) {
      return;
    }

    // Only show loading state when we're actually going to save
    if (fieldId && this.fieldsNeedingSave.has(fieldId)) {
      this.setFieldSaving(fieldId, true);
    }

    if (immediate) {
      // Save immediately for checkbox/radio changes
      this.saveHorseNote();
    } else {
      // Use debounced save for text input
      this.autoSaveSubject.next();
    }
  }

  // Update saveHorseNote to clear the fieldsNeedingSave set
  saveHorseNote(fieldId?: string): void {
    this.toastService.clear();
    this.isAddedSucessfully = false;
    if (!this.auctionItemId || this.isSaving) {
      return;
    }

    if (!this.shouldSave()) {
      this.noteFields.forEach((field) => {
        this.setFieldSaving(field.id, false);
      });
      // Clear the fields needing save
      this.fieldsNeedingSave.clear();
      return;
    }

    if (fieldId == 'notes') {
      this.setFieldSaving(fieldId, true);
    }

    this.isSaving = true;
    const formData = this.getAllFormData();

    const requestData: any = {
      auction_item_id: this.auctionItemId,
      notes: formData['notes'] || '',
      rating: formData['rates'] || '',
      confirmation_type: formData['conformation'] || [],
      scope_type: formData['scope'] || [],
    };

    this.auctionService.saveHorseNote(requestData).subscribe({
      next: (response) => {
        this.toastService.showSuccess(response.message);
        this.isSaving = false;
        this.isAddedSucessfully = true;
        setTimeout(() => {
          this.isAddedSucessfully = false;
        }, 5000);

        // Create updated horse notes data to emit immediately
        const updatedNotes = {
          rating: formData['rates'] || null,
          confirmation: {
            types: formData['conformation'] || [],
          },
          scope: {
            types: formData['scope'] || [],
          },
          notes: formData['notes'] || null,
        };

        // Emit the updated data immediately for UI update
        this.notesUpdated.emit(updatedNotes);

        // Also emit the traditional event for backward compatibility
        this.notesChanged.emit('true');

        this.closeAllDropdowns();
        this.noteFields.forEach((field) => {
          this.setFieldSaving(field.id, false);
        });
        // Clear the fields needing save after successful save
        this.fieldsNeedingSave.clear();
        this.isDoneButtonDisabled = true;
      },
      error: (error) => {
        this.isSaving = false;
        this.toastService.showError(error.error.error[0]);
        this.noteFields.forEach((field) => {
          this.setFieldSaving(field.id, false);
        });
        // Don't clear fieldsNeedingSave on error, so user can try again
      },
    });
  }

  private resetComponentState(): void {
    // Reset dropdown states
    this.initializeDropdownStates();

    // Reset other state variables
    this.isTextNotePanel = false;
    this.isDoneButtonDisabled = true;
    this.fieldSavingStates = {};
    this.dropdownPositions = {};
    this.fieldsNeedingSave.clear();
    this.initializedFields.clear(); // Clear initialized fields tracking

    // Close any open dropdowns
    this.closeAllDropdowns();
  }
}
